import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
   Checkbox,
   DialogActions,
   DialogContent,
   DialogTitle,
   FormControlLabel,
   IconButton,
   Step,
   StepLabel,
   Stepper,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DeleteIcon16pxSvg from '../../../assets/images/icons/DeleteIcon16pxSvg';
import { editShift } from '../../../axios/Shift/ShiftApi';
import CustomDatePicker from '../../../components/CustomDatePicker';
import CustomInput from '../../../components/CustomInput';
import CustomTimePicker from '../../../components/CustomTimePicker';
import {
   ControlpanelPrimary3thButton,
   ControlpanelPrimaryButton,
   ControlpanelSecondaryButton,
   ControlpanelSuccess3thButton,
   GrayIconButton,
} from '../../../components/StyledButton';
import { CustomDialog, NormalSwitch } from '../../../components/StyledComponents';
import { useLoading } from '../../../context/LoadingContext';
import { calculateDuration, calculateEndTime } from '../../../utils/DisplayTimeString/TimeString';
import { formatStringToHourMiniteString } from '../../../utils/FormatDateTimeString/dateUtils';

const STEP_COUNT = 3;

const normalizeBreakTimes = (breakTimes = []) =>
   breakTimes.map((breakTime) => ({
      ...breakTime,
      breakStart: formatStringToHourMiniteString(breakTime.breakStart),
      durationHours: formatStringToHourMiniteString(breakTime.durationHours),
   }));

const stepperSx = {
   '& .MuiStepLabel-label.Mui-active': {
      color: '#0057FF',
      fontWeight: 600,
   },
   '& .MuiStepLabel-label.Mui-completed': {
      color: '#0057FF',
      fontWeight: 600,
   },
   '& .MuiStepIcon-root.Mui-active': {
      color: '#0057FF',
   },
   '& .MuiStepIcon-root.Mui-completed': {
      color: '#0057FF',
   },
   '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
      borderColor: '#0057FF',
   },
};

const EditShiftDialog = (props) => {
   const { t } = useTranslation();
   const [show, hide] = useLoading();
   const { open, handleClose, selectedEditShift, onRefresh } = props;
   const [activeStep, setActiveStep] = useState(0);
   const [isValid, setIsValid] = useState(false);
   const [workHoursModified, setWorkHoursModified] = useState(false);
   const [maxTimeValue, setMaxTimeValue] = useState(24);
   const [applyChangeStartingFrom, setApplyChangeStartingFrom] = useState(new Date());

   const initialFormData = {
      shiftId: '',
      shiftName: '',
      description: '',
      shiftStart: '00:00',
      durationHours: '24:00',
      workHours: '08:00',
      breakTimes: [],
      sendMailCheckInStart: '00:00',
      sendMailCheckInEnd: '00:00',
      sendMailCheckOutStart: '00:00',
      sendMailCheckOutEnd: '00:00',
      restrictCheckInStart: '00:00',
      restrictCheckInEnd: '24:00',
      restrictCheckOutStart: '24:00',
      restrictCheckOutEnd: '00:00',
      restrictStartOT: '00:00',
      restrictEndOT: '00:00',
      maxOT: '00:00',
   };

   const initialPendingBreak = {
      breakName: '',
      breakStart: '00:00',
      durationHours: '01:00',
   };

   const [formData, setFormData] = useState(initialFormData);
   const [pendingBreak, setPendingBreak] = useState(initialPendingBreak);

   const initialCheckedItems = {
      sendMailCheckIn: false,
      sendMailCheckOut: false,
      restrictCheckIn: false,
      restrictCheckOut: false,
      restrictOvertimeHours: false,
      maximumOTHoursAllowed: false,
      applyChangeToShiftToOldEventLog: false,
   };

   const [checkedItems, setCheckedItems] = useState(initialCheckedItems);

   const [validationMessages, setValidationMessages] = useState({
      shiftName: '',
      description: '',
   });

   const [isError, setIsError] = useState({
      shiftName: false,
      description: false,
      breakTimes: [],
   });

   const steps = useMemo(
      () => [
         t('shiftWizard.shift-details'),
         t('shiftWizard.break-time'),
         t('shiftWizard.additional-settings'),
      ],
      [t]
   );

   const resetDialog = () => {
      setActiveStep(0);
      setPendingBreak(initialPendingBreak);
      setValidationMessages({ shiftName: '', description: '' });
      setWorkHoursModified(false);
      setMaxTimeValue(24);
   };

   const handleCloseDialog = () => {
      resetDialog();
      handleClose();
   };

   useEffect(() => {
      if (!selectedEditShift || !open) return;

      const loadedDuration = formatStringToHourMiniteString(selectedEditShift.durationHours, 'HH:mm') || '24:00';
      const loadedWorkHours = formatStringToHourMiniteString(selectedEditShift.workHours, 'HH:mm') || '08:00';

      setCheckedItems((prevCheckedItems) => ({
         ...prevCheckedItems,
         sendMailCheckIn: !!(selectedEditShift.sendMailCheckInStart && selectedEditShift.sendMailCheckInEnd),
         sendMailCheckOut: !!(selectedEditShift.sendMailCheckOutStart && selectedEditShift.sendMailCheckOutEnd),
         restrictCheckIn: !!(selectedEditShift.restrictCheckInStart && selectedEditShift.restrictCheckInEnd),
         restrictCheckOut: !!(selectedEditShift.restrictCheckOutStart && selectedEditShift.restrictCheckOutEnd),
         restrictOvertimeHours: !!(selectedEditShift.restrictStartOT && selectedEditShift.restrictEndOT),
         maximumOTHoursAllowed: !!selectedEditShift.maxOT,
      }));

      setFormData({
         shiftId: selectedEditShift.shiftId || '',
         shiftName: selectedEditShift.shiftName || '',
         description: selectedEditShift.description || '',
         shiftStart: formatStringToHourMiniteString(selectedEditShift.shiftStart, 'HH:mm') || '00:00',
         durationHours: loadedDuration,
         workHours: loadedWorkHours,
         breakTimes: normalizeBreakTimes(selectedEditShift.breakTimes),
         sendMailCheckInStart: formatStringToHourMiniteString(selectedEditShift.sendMailCheckInStart, 'HH:mm') || '00:00',
         sendMailCheckInEnd: formatStringToHourMiniteString(selectedEditShift.sendMailCheckInEnd, 'HH:mm') || '00:00',
         sendMailCheckOutStart: formatStringToHourMiniteString(selectedEditShift.sendMailCheckOutStart, 'HH:mm') || '00:00',
         sendMailCheckOutEnd: formatStringToHourMiniteString(selectedEditShift.sendMailCheckOutEnd, 'HH:mm') || '00:00',
         restrictCheckInStart: formatStringToHourMiniteString(selectedEditShift.restrictCheckInStart, 'HH:mm') || '00:00',
         restrictCheckInEnd: formatStringToHourMiniteString(selectedEditShift.restrictCheckInEnd, 'HH:mm') || '24:00',
         restrictCheckOutStart: formatStringToHourMiniteString(selectedEditShift.restrictCheckOutStart, 'HH:mm') || '24:00',
         restrictCheckOutEnd: formatStringToHourMiniteString(selectedEditShift.restrictCheckOutEnd, 'HH:mm') || '00:00',
         restrictStartOT: formatStringToHourMiniteString(selectedEditShift.restrictStartOT, 'HH:mm') || '00:00',
         restrictEndOT: formatStringToHourMiniteString(selectedEditShift.restrictEndOT, 'HH:mm') || '00:00',
         maxOT: formatStringToHourMiniteString(selectedEditShift.maxOT, 'HH:mm') || '00:00',
      });

      setWorkHoursModified(loadedWorkHours !== loadedDuration);
      setMaxTimeValue(parseInt(loadedDuration.split(':')[0], 10) || 24);
      setActiveStep(0);
      setPendingBreak(initialPendingBreak);
      setValidationMessages({ shiftName: '', description: '' });
   }, [open, selectedEditShift]);

   useEffect(() => {
      const { shiftName, description } = formData;
      const isShiftNameValid = shiftName !== '' && validationMessages.shiftName === '';
      const isDescriptionValid = description !== '' && validationMessages.description === '';
      const isBreakTimesValid = !isError.breakTimes.some((error) => error);

      setIsError((prevError) => ({
         ...prevError,
         shiftName: isShiftNameValid ? null : true,
         description: isDescriptionValid ? null : true,
      }));

      setIsValid(isShiftNameValid && isDescriptionValid && isBreakTimesValid);
   }, [formData, validationMessages, isError.breakTimes]);

   const shiftEndTime = calculateEndTime(formData.shiftStart, formData.durationHours);
   const { days, hours, minutes } = calculateDuration(formData.durationHours);
   const pendingBreakEnd = calculateEndTime(pendingBreak.breakStart, pendingBreak.durationHours);
   const pendingDuration = calculateDuration(pendingBreak.durationHours);

   const isShiftEndNextDay = useMemo(() => {
      const [startH] = formData.shiftStart.split(':').map(Number);
      const [durationH] = formData.durationHours.split(':').map(Number);
      return durationH >= 24 || startH + durationH >= 24;
   }, [formData.shiftStart, formData.durationHours]);

   const updateFormDataAndValidate = (name, value) => {
      setFormData((prevData) => {
         const updatedData = { ...prevData, [name]: value };

         if (name === 'shiftName') {
            setValidationMessages((prev) => ({
               ...prev,
               shiftName: value === '' ? 'Shift name is required' : '',
            }));
         }

         if (name === 'description') {
            setValidationMessages((prev) => ({
               ...prev,
               description: value === '' ? 'Description is required' : '',
            }));
         }

         return updatedData;
      });
   };

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      updateFormDataAndValidate(name, value);
   };

   const handlePendingBreakInputChange = (e) => {
      const { name, value } = e.target;
      setPendingBreak((prev) => ({ ...prev, [name]: value }));
   };

   const handlePendingBreakTimeChange = (name, value) => {
      setPendingBreak((prev) => ({ ...prev, [name]: value }));
   };

   const handleAddBreakFromForm = () => {
      if (!pendingBreak.breakName.trim()) {
         toast.error(`${t('common.break-name')} ${t('common.is-required')}`);
         return;
      }

      setFormData((prev) => ({
         ...prev,
         breakTimes: [
            ...prev.breakTimes,
            {
               breakName: pendingBreak.breakName.trim(),
               breakStart: pendingBreak.breakStart,
               durationHours: pendingBreak.durationHours,
            },
         ],
      }));

      setPendingBreak(initialPendingBreak);
   };

   const handleRemoveBreakRow = (index) => {
      setFormData((prev) => ({
         ...prev,
         breakTimes: prev.breakTimes.filter((_, i) => i !== index),
      }));
   };

   const handleTimeChange = (name, value) => {
      if (name === 'durationHours') {
         const hour = parseInt(value.split(':')[0], 10);
         setMaxTimeValue(hour);
         setWorkHoursModified(false);
      }

      if (name === 'workHours') {
         setWorkHoursModified(true);
      }

      setFormData((prev) => ({ ...prev, [name]: value }));
   };

   useEffect(() => {
      if (!workHoursModified) {
         setFormData((prev) => ({
            ...prev,
            workHours: prev.durationHours,
         }));
      }
   }, [formData.durationHours, workHoursModified]);

   const buildSubmitPayload = (checkedOverride) => {
      const items = checkedOverride || checkedItems;
      let submitData = { ...formData, langId: localStorage.getItem('i18nextLngId') };

      if (!items.sendMailCheckIn) {
         submitData.sendMailCheckInStart = null;
         submitData.sendMailCheckInEnd = null;
      }

      if (!items.sendMailCheckOut) {
         submitData.sendMailCheckOutStart = null;
         submitData.sendMailCheckOutEnd = null;
      }

      if (!items.restrictCheckIn) {
         submitData.restrictCheckInStart = null;
         submitData.restrictCheckInEnd = null;
      }

      if (!items.restrictCheckOut) {
         submitData.restrictCheckOutStart = null;
         submitData.restrictCheckOutEnd = null;
      }

      if (!items.restrictOvertimeHours) {
         submitData.restrictStartOT = null;
         submitData.restrictEndOT = null;
      }

      if (!items.maximumOTHoursAllowed) {
         submitData.maxOT = null;
      }

      return submitData;
   };

   const handleSubmit = async (e, options = {}) => {
      e?.preventDefault?.();

      const checkedOverride = options.skipAdditionalSettings
         ? {
            ...checkedItems,
            sendMailCheckIn: false,
            sendMailCheckOut: false,
            restrictCheckIn: false,
            restrictCheckOut: false,
            restrictOvertimeHours: false,
            maximumOTHoursAllowed: false,
         }
         : checkedItems;

      const submitData = buildSubmitPayload(checkedOverride);

      show();
      try {
         const response = await editShift(submitData);
         if (response.data.message === 'Successful' || response.data.message === 'Thành công') {
            handleCloseDialog();
            toast.success(t('common.update-shift-successfully'));
            onRefresh();
         } else {
            toast.error(response.data.message);
         }
      } catch (error) {
         if (error.response) {
            if (error.response.status !== 401) {
               toast.error(t(error.response.data));
            }
         }
         console.error('There was an error editing shift!', error);
      } finally {
         hide();
      }
   };

   const validateStep0 = () => {
      const shiftNameMsg = formData.shiftName === '' ? 'Shift name is required' : '';
      const descriptionMsg = formData.description === '' ? 'Description is required' : '';

      setValidationMessages({
         shiftName: shiftNameMsg,
         description: descriptionMsg,
      });

      return !shiftNameMsg && !descriptionMsg;
   };

   const handleNext = () => {
      if (activeStep === 0 && !validateStep0()) {
         return;
      }
      setActiveStep((prev) => Math.min(prev + 1, STEP_COUNT - 1));
   };

   const handleBack = () => {
      setActiveStep((prev) => Math.max(prev - 1, 0));
   };

   const handleDoLater = (e) => {
      if (activeStep === 0 && !validateStep0()) {
         return;
      }
      if (isDoLaterDisabled) {
         return;
      }
      handleSubmit(e, { skipAdditionalSettings: true });
   };

   const isDoLaterDisabled = useMemo(() => {
      if (activeStep === 1) {
         return (
            pendingBreak.breakName.trim() !== '' ||
            formData.breakTimes.length > 0 ||
            pendingBreak.breakStart !== initialPendingBreak.breakStart ||
            pendingBreak.durationHours !== initialPendingBreak.durationHours
         );
      }

      if (activeStep === 2) {
         return (
            checkedItems.sendMailCheckIn ||
            checkedItems.sendMailCheckOut ||
            checkedItems.restrictCheckIn ||
            checkedItems.restrictCheckOut ||
            checkedItems.restrictOvertimeHours ||
            checkedItems.maximumOTHoursAllowed
         );
      }

      return false;
   }, [activeStep, pendingBreak, formData.breakTimes, checkedItems]);

   const renderSettingToggle = (
      label,
      checked,
      onToggle,
      children,
      disabled = false
   ) => (
      <div className="space-y-4 py-2">
         <div className="flex items-center justify-between gap-4">
            <p
               className={`text-sm font-medium ${disabled ? 'text-[#9CA3AF]' : 'text-[#111827]'
                  }`}
            >
               {label}
            </p>
            <NormalSwitch
               checked={checked}
               onChange={onToggle}
               disabled={disabled}
            />
         </div>

         <div className={disabled ? 'pointer-events-none opacity-50' : ''}>
            {children}
         </div>
      </div>
   );

   const renderTimeField = (label, subLabel, name, value, maxHours, hoursLabel = false, disabled = false) => (
      <div className="flex items-center justify-between gap-6">
         <div className="min-w-0 flex-1">
            <label className="block text-sm font-medium leading-tight">
               {label} <span className="text-red-500">*</span>
            </label>
            {subLabel && (
               <span className="block text-xs text-[#6B7280] mt-0.5 leading-tight">{subLabel}</span>
            )}
         </div>
         <div className="w-[120px] flex-shrink-0">
            <CustomTimePicker
               hoursLabel={false}
               secondsEnabled={false}
               readOnly
               disabled={disabled}
               onTimeChange={handleTimeChange}
               value={value}
               name={name}
               maxHours={maxHours}
            />
         </div>
      </div>
   );

   const renderStep0 = () => (
      <div className="space-y-5 pt-2">
         <CustomInput
            required
            title={t('common.shift-name')}
            inputType="text"
            name="shiftName"
            placeholder={t('common.shift-name')}
            message={validationMessages.shiftName}
            handleInputChange={handleInputChange}
            value={formData.shiftName}
         />
         <CustomInput
            required
            title={t('common.description')}
            inputType="text"
            name="description"
            placeholder={t('common.description')}
            message={validationMessages.description}
            handleInputChange={handleInputChange}
            value={formData.description}
         />

         <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
               <label className="block text-sm font-medium mb-2">
                  {t('common.shift-start')} <span className="text-red-500">*</span>
               </label>
               <CustomTimePicker
                  hoursLabel={false}
                  secondsEnabled={false}
                  readOnly
                  onTimeChange={handleTimeChange}
                  value={formData.shiftStart}
                  name="shiftStart"
                  maxHours={23}
               />
               <p className="text-xs text-[#6B7280] mt-2">
                  {t('common.shift-end')}{' '}
                  <span className="font-semibold text-[#9CA3AF]">
                     {shiftEndTime}
                     {isShiftEndNextDay ? ` ${t('common.next-day')}` : ''}
                  </span>
               </p>
            </div>

            <div>
               <label className="block text-sm font-medium mb-2">
                  {t('common.duration-hours')} <span className="text-red-500">*</span>
               </label>
               <CustomTimePicker
                  hoursLabel={false}
                  secondsEnabled={false}
                  readOnly
                  onTimeChange={handleTimeChange}
                  value={formData.durationHours}
                  name="durationHours"
                  maxHours={24}
               />
               <p className="text-xs text-[#9CA3AF] mt-2">
                  {days > 0 && `${days} ${t('common.days')}, `}
                  {hours} {t('common.hours')} {t('common.and', { defaultValue: 'and' })} {minutes} {t('common.minutes')}
               </p>
            </div>

            <div>
               <label className="block text-sm font-medium mb-2">
                  {t('common.work-hours')} <span className="text-red-500">*</span>
               </label>
               <CustomTimePicker
                  hoursLabel={false}
                  secondsEnabled={false}
                  readOnly
                  onTimeChange={handleTimeChange}
                  value={formData.workHours}
                  name="workHours"
                  maxHours={maxTimeValue}
               />
            </div>
         </div>
      </div>
   );

   const renderStep1 = () => (
      <div className="space-y-5 pt-2">
         <CustomInput
            required
            title={t('common.break-name')}
            inputType="text"
            name="breakName"
            placeholder={t('common.shift-name')}
            handleInputChange={handlePendingBreakInputChange}
            value={pendingBreak.breakName}
         />

         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
               <label className="block text-sm font-medium mb-2">
                  {t('common.break-start')} <span className="text-red-500">*</span>
               </label>
               <CustomTimePicker
                  hoursLabel={false}
                  secondsEnabled={false}
                  readOnly
                  onTimeChange={handlePendingBreakTimeChange}
                  value={pendingBreak.breakStart}
                  name="breakStart"
                  maxHours={23}
               />
               <p className="text-xs text-[#6B7280] mt-2">
                  {t('common.break-end')}{' '}
                  <span className="font-semibold text-[#111827]">{pendingBreakEnd}</span>
               </p>
            </div>

            <div>
               <label className="block text-sm font-medium mb-2">
                  {t('common.duration-hours')} <span className="text-red-500">*</span>
               </label>
               <CustomTimePicker
                  hoursLabel={false}
                  secondsEnabled={false}
                  readOnly
                  onTimeChange={handlePendingBreakTimeChange}
                  value={pendingBreak.durationHours}
                  name="durationHours"
                  maxHours={24}
               />
               <p className="text-xs text-[#6B7280] mt-2">
                  {pendingDuration.days > 0 && `${pendingDuration.days} ${t('common.days')}, `}
                  {pendingDuration.hours} {t('common.hours')} {t('common.and', { defaultValue: 'and' })} {pendingDuration.minutes} {t('common.minutes')}
               </p>
            </div>
         </div>

         <div className="flex justify-end">
            <ControlpanelPrimary3thButton className="h-10" disableRipple onClick={handleAddBreakFromForm}>
               <AddIcon />
               <p>{t('button.add-break-time')}</p>
            </ControlpanelPrimary3thButton>
         </div>

         {formData.breakTimes.length > 0 && (
            <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
               <table className="w-full text-sm">
                  <thead className="bg-[#F9FAFB]">
                     <tr>
                        <th className="text-left px-4 py-3 font-semibold">{t('common.name')}</th>
                        <th className="text-left px-4 py-3 font-semibold">{t('common.start')}</th>
                        <th className="text-left px-4 py-3 font-semibold">{t('common.duration')}</th>
                        <th className="text-left px-4 py-3 font-semibold">{t('common.end')}</th>
                        <th className="text-center px-4 py-3 font-semibold w-16" />
                     </tr>
                  </thead>
                  <tbody>
                     {formData.breakTimes.map((breakTime, index) => (
                        <tr key={`${breakTime.breakName}-${index}`} className="border-t border-[#E5E7EB]">
                           <td className="px-4 py-3">{breakTime.breakName}</td>
                           <td className="px-4 py-3">{breakTime.breakStart}</td>
                           <td className="px-4 py-3">{breakTime.durationHours}</td>
                           <td className="px-4 py-3">
                              {calculateEndTime(breakTime.breakStart, breakTime.durationHours)}
                           </td>
                           <td className="px-4 py-3 text-center">
                              <IconButton
                                 size="small"
                                 onClick={() => handleRemoveBreakRow(index)}
                                 className="!text-[#D00D00]"
                              >
                                 <DeleteIcon16pxSvg width={20} height={20} fontSize="small" color="#D00D00" />
                              </IconButton>
                           </td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
         )}
      </div>
   );

   const renderStep2 = () => (
      <div className="space-y-2 pt-2">
         {renderSettingToggle(
            t('common.send-notification-check-in-in-the-below-interval'),
            checkedItems.sendMailCheckIn,
            (e) => setCheckedItems({ ...checkedItems, sendMailCheckIn: e.target.checked }),
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {renderTimeField(
                  t('common.check-in-start'),
                  t('common.hours-before-shift-start'),
                  'sendMailCheckInStart',
                  formData.sendMailCheckInStart,
                  23,
                  false,
                  !checkedItems.sendMailCheckIn
               )}
               {renderTimeField(
                  t('common.check-in-end'),
                  t('common.hours-after-shift-start'),
                  'sendMailCheckInEnd',
                  formData.sendMailCheckInEnd,
                  23,
                  true,
                  !checkedItems.sendMailCheckIn
               )}
            </div>
         )}

         {renderSettingToggle(
            t('common.send-notification-check-out-in-the-below-interval'),
            checkedItems.sendMailCheckOut,
            (e) => setCheckedItems({ ...checkedItems, sendMailCheckOut: e.target.checked }),
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {renderTimeField(
                  t('common.check-out-start'),
                  t('common.hours-before-shift-end'),
                  'sendMailCheckOutStart',
                  formData.sendMailCheckOutStart,
                  23,
                  false,
                  !checkedItems.sendMailCheckOut
               )}
               {renderTimeField(
                  t('common.check-out-end'),
                  t('common.hours-after-shift-end'),
                  'sendMailCheckOutEnd',
                  formData.sendMailCheckOutEnd,
                  23,
                  true,
                  !checkedItems.sendMailCheckOut
               )}
            </div>
         )}

         {renderSettingToggle(
            t('common.restrict-check-in-in-the-below-interval'),
            checkedItems.restrictCheckIn,
            (e) => setCheckedItems({ ...checkedItems, restrictCheckIn: e.target.checked }),
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {renderTimeField(
                  t('common.start'),
                  t('common.hours-before-shift-start'),
                  'restrictCheckInStart',
                  formData.restrictCheckInStart,
                  23,
                  false,
                  !checkedItems.restrictCheckIn
               )}
               {renderTimeField(
                  t('common.end'),
                  t('common.hours-after-shift-start'),
                  'restrictCheckInEnd',
                  formData.restrictCheckInEnd,
                  168,
                  true,
                  !checkedItems.restrictCheckIn
               )}
            </div>, true
         )}

         {renderSettingToggle(
            t('common.restrict-check-out-in-the-below-interval'),
            checkedItems.restrictCheckOut,
            (e) => setCheckedItems({ ...checkedItems, restrictCheckOut: e.target.checked }),
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {renderTimeField(
                  t('common.start'),
                  t('common.hours-before-shift-end'),
                  'restrictCheckOutStart',
                  formData.restrictCheckOutStart,
                  48,
                  false,
                  !checkedItems.restrictCheckOut
               )}
               {renderTimeField(
                  t('common.end'),
                  t('common.hours-after-shift-end'),
                  'restrictCheckOutEnd',
                  formData.restrictCheckOutEnd,
                  160,
                  true,
                  !checkedItems.restrictCheckOut
               )}
            </div>, true
         )}

         {renderSettingToggle(
            t('common.restrict-overtime-hours'),
            checkedItems.restrictOvertimeHours,
            (e) => setCheckedItems({ ...checkedItems, restrictOvertimeHours: e.target.checked }),
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {renderTimeField(
                  t('common.start'),
                  t('common.from-shift-end'),
                  'restrictStartOT',
                  formData.restrictStartOT,
                  168,
                  false,
                  !checkedItems.restrictOvertimeHours
               )}
               {renderTimeField(
                  t('common.end'),
                  t('common.from-shift-end'),
                  'restrictEndOT',
                  formData.restrictEndOT,
                  168,
                  true,
                  !checkedItems.restrictOvertimeHours
               )}
            </div>, true
         )}

         <div className="flex items-center justify-between gap-4 py-2">
            <p className="text-sm font-medium text-[#111827] min-w-0 flex-1">
               {t('common.maximum-ot-hours-allowed')}
            </p>
            <div className="flex items-center gap-3 flex-shrink-0">
               <div className="w-[180px]">
                  <CustomTimePicker
                     hoursLabel={false}
                     secondsEnabled={false}
                     readOnly
                     disabled={!checkedItems.maximumOTHoursAllowed}
                     onTimeChange={handleTimeChange}
                     value={formData.maxOT}
                     name="maxOT"
                     maxHours={24}
                  />
               </div>
               <NormalSwitch
                  checked={checkedItems.maximumOTHoursAllowed}
                  onChange={(e) =>
                     setCheckedItems({ ...checkedItems, maximumOTHoursAllowed: e.target.checked })
                  }
                  disabled
               />
            </div>
         </div>
      </div>
   );

   return (
      <CustomDialog maxWidth="lg" fullWidth open={open} onClose={handleCloseDialog} keepMounted>
         <DialogTitle>
            <div className="flex justify-between items-center">
               <p className="text-xl font-semibold">{t('common.edit-shift')}</p>
               <GrayIconButton onClick={handleCloseDialog}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>

         <DialogContent>
            <div className="mt-4 mb-6">
               <Stepper activeStep={activeStep} alternativeLabel sx={stepperSx}>
                  {steps.map((label) => (
                     <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                     </Step>
                  ))}
               </Stepper>
            </div>

            {activeStep === 0 && renderStep0()}
            {activeStep === 1 && renderStep1()}
            {activeStep === 2 && renderStep2()}
         </DialogContent>

         <DialogActions>
            <div className="flex w-full justify-between items-center gap-2 px-4 py-4">
               {activeStep === 0 ? (
                  <ControlpanelSecondaryButton onClick={handleCloseDialog}>
                     <p>{t('button.cancel')}</p>
                  </ControlpanelSecondaryButton>
               ) : (
                  <ControlpanelSecondaryButton onClick={handleBack}>
                     <p>{t('button.back')}</p>
                  </ControlpanelSecondaryButton>
               )}

               <div className="flex items-center gap-3 ml-auto">
                  {activeStep > 0 && (
                     <ControlpanelSuccess3thButton onClick={handleDoLater} disabled={isDoLaterDisabled}>
                        <p>{t('button.do-later')}</p>
                     </ControlpanelSuccess3thButton>
                  )}

                  {activeStep < STEP_COUNT - 1 ? (
                     <ControlpanelPrimaryButton onClick={handleNext} disabled={activeStep === 0 && !isValid}>
                        <p>{t('button.next')}</p>
                     </ControlpanelPrimaryButton>
                  ) : (
                     <ControlpanelPrimaryButton onClick={handleSubmit} disabled={!isValid}>
                        <p>{t('button.update')}</p>
                     </ControlpanelPrimaryButton>
                  )}
               </div>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default EditShiftDialog;
